<template>
    <v-container fluid>
        <!-- Breadcrumbs -->
        <v-breadcrumbs :items="breadcrumbs" class="mb-2" />

        <!-- Header -->
        <div class="d-flex align-center mb-4 px-6">
            <h2 class="font-weight-bold">{{ $t('carbonCredit.plotTreeMapping.request') }} ({{ request.request_id }})</h2>
            <v-spacer />
            <v-btn color="error" outlined class="mr-2" @click="openCancelRequestConfirm">{{
                $t('carbonCredit.plotTreeMapping.cancelRequest')}}</v-btn>
            <v-btn color="primary" class="mr-2" @click="goBack">{{ $t('back') }}</v-btn>
            <v-btn color="primary" class="mr-2" @click="editRequest">{{ $t('edit') }}</v-btn>
            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn color="success" outlined class="mr-2" v-bind="attrs" v-on="on">
                        <v-icon left>mdi-download</v-icon> {{ $t('download') }}
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item @click="download('pdf')"><v-list-item-title>Download as
                            PDF</v-list-item-title></v-list-item>
                    <v-list-item @click="download('xls')"><v-list-item-title>Download as
                            XLS</v-list-item-title></v-list-item>
                </v-list>
            </v-menu>
            <v-btn v-if="request.status !== 'completed'" color="primary" @click="openMarkCompletedConfirm">MARK AS COMPLETED</v-btn>
        </div>

        <div class="px-6">

            <!-- Expansion Panels for all sections -->
            <v-expansion-panels multiple expand v-model="expandedPanels">
                <!-- Request Detail Card -->
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div class="font-weight-bold">Plot Tree Mapping Request Detail</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-row>
                            <v-col cols="4">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Start Date <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The date when the plot tree mapping request was created.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ formatDate(request.start_date) }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="4">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Due Date <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The date when the plot tree mapping request is due.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ formatDate(request.due_date) }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="4">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Status <v-tooltip bottom max-width="280">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span><strong>Plot Tree Request Statuses:</strong><br/>
                                            • <strong>Pending</strong> – Admin sent request to user to submit plot tree details<br/>
                                            • <strong>Partially Submitted</strong> – User partially submitted details for plots<br/>
                                            • <strong>Submitted</strong> – User submitted details for all plots<br/>
                                            • <strong>Completed</strong> – Admin marked the request as completed</span>
                                        </v-tooltip></p>
                                    <v-btn small rounded min-width="120" depressed :class="getRequestStatusClass(request.status)" height="40">
                                        {{ formatStatus(request.status) }}
                                    </v-btn>
                                </v-card>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">{{ $t('carbonCredit.plotTreeMapping.project') }}</p>
                                    <p class="">{{ request.project?.project_title || 'N/A' }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="6">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Farm Location Address <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The address of the farm where the plot tree mapping request is
                                                located.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ request.farm?.address || 'N/A' }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="6">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Description <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The description of the plot tree mapping request.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ request.notes || 'N/A' }}</p>
                                </v-card>
                            </v-col>

                            <v-col cols="12" class="mt-4">
                                <v-data-table :key="requestRefreshKey" :headers="plotHeaders" :items="request.plots || []" class="elevation-0"
                                    :items-per-page="-1"
                                    hide-default-footer>
                                    <template v-slot:item.coordinates="{ item }">
                                        <div>
                                            <div>{{ item.longitude }}, {{ item.latitude }}</div>
                                        </div>
                                    </template>
                                    <template v-slot:item.radius="{ item }">
                                        <div>{{ item.radius != null ? (item.radius / 100) + ' m' : 'N/A' }}</div>
                                    </template>
                                    <template v-slot:item.status="{ item }">
                                        <v-btn small rounded min-width="120" depressed :class="getPlotStatusClass(getPlotStatus(item))" height="40">
                                            {{ formatStatus(getPlotStatus(item)) }}
                                        </v-btn>
                                    </template>
                                    <template v-slot:item.actions="{ item }">
                                        <v-menu offset-y>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon v-bind="attrs"
                                                    v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
                                            </template>
                                            <v-list>
                                                <v-list-item @click="viewPlot(item)">
                                                    <v-list-item-title>View</v-list-item-title>
                                                </v-list-item>
                                                <v-list-item @click="downloadPlotReport(item, 'pdf')">
                                                    <v-list-item-title>Download</v-list-item-title>
                                                </v-list-item>
                                                <v-list-item @click="deletePlot(item)">
                                                    <v-list-item-title class="error--text">Remove</v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </template>
                                </v-data-table>

                                <v-divider v-if="plotsForMap.length" class="plot-table-map-divider" />
                                <div v-if="plotsForMap.length" class="plot-map-below-table">
                                    <GmapMap
                                        ref="plotViewGmap"
                                        :center="{ lat: 20.5937, lng: 78.9629 }"
                                        :zoom="4"
                                        map-type-id="satellite"
                                        style="width: 100%; height: 500px"
                                    >
                                        <GmapCluster>
                                            <GmapMarker
                                                v-for="plot in plotsForMap"
                                                :key="plot.id"
                                                :position="{ lat: parseFloat(plot.latitude), lng: parseFloat(plot.longitude) }"
                                                :clickable="true"
                                                :icon="getPlotMapIcon(plot)"
                                                :title="String(plot.plot_no || '')"
                                                @click="viewPlot(plot)"
                                            />
                                        </GmapCluster>
                                    </GmapMap>

                                    <div class="status-legend-section plot-tree-status-legend mt-4 pa-4">
                                        <div class="legend-title font-weight-bold mr-3 d-flex align-center flex-wrap">
                                            {{ $t('carbonCredit.plotTreeMapping.plotLegendTitle') }}
                                            <v-tooltip top color="#00BD73" max-width="350">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon size="16" class="icon-with-background primary--text ml-1" v-bind="attrs" v-on="on">
                                                        mdi-information
                                                    </v-icon>
                                                </template>
                                                <span>{{ $t('carbonCredit.plotTreeMapping.plotMarkerLegendInfo') }}</span>
                                            </v-tooltip>
                                        </div>
                                        <div class="legend-item d-inline-flex align-center flex-wrap mr-4 mt-2">
                                            <span
                                                v-for="item in plotMarkerLegendItems"
                                                :key="item.key"
                                                class="legend-item d-inline-flex align-center mr-4 mt-2"
                                            >
                                                <img
                                                    :src="item.iconDataUrl"
                                                    alt=""
                                                    class="plot-marker-legend-icon flex-shrink-0"
                                                >
                                                <span>{{ $t(item.labelKey) }}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Assignee -->
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div class="font-weight-bold">Assignee</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-row v-if="request.assignees && request.assignees.length > 0">
                            <v-col cols="6" v-for="(assignee, index) in request.assignees" :key="index">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <div class="d-flex justify-space-between align-center">
                                        <div>
                                            <p class="font-weight-bold">{{ assignee.assignee_role }} <v-tooltip bottom>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-icon v-bind="attrs" v-on="on"
                                                            size="16">mdi-information-outline</v-icon>
                                                    </template>
                                                    <span>The role of the assignee.</span>
                                                </v-tooltip></p>
                                            <p class="">{{ assignee.assignee.fullName }}</p>
                                       
                                        </div>
                                    </div>
                                </v-card>
                            </v-col>
                        </v-row>
                        <v-row v-else>
                            <v-col cols="12">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="text-grey">No assignees assigned</p>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Farm Information -->
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div class="font-weight-bold">Farm Information</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-row>
                            <v-col cols="4">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Farm Name <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The name of the farm.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ request.farm?.farmName || 'N/A' }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="4">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Dimitra Farm ID <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The ID of the farm.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ request.farm?.dimitraFarmId || 'N/A' }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="4">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Crop Growing Practices <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The registration number of the farm.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ request.farm?.registrationNo || 'N/A' }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="4">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Status <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The status of the farm.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ formatStatus(request.farm?.status) }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="4">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Farm Geofence <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The geofence of the farm.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ request.farm?.farmGeofenceName || 'N/A' }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="4">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Farm Location Address <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The address of the farm.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ request.farm?.address || 'N/A' }}</p>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Farmer Information -->
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div class="font-weight-bold">Farmer Information</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-row>
                            <v-col cols="3">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Farmer First Name <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The first name of the farmer.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ request.farmer?.firstName || 'N/A' }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="3">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Farmer Middle Name <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The middle name of the farmer.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ request.farmer?.middleName || 'N/A' }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="3">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Farmer Last Name <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The last name of the farmer.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ request.farmer?.lastName || 'N/A' }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="3">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Farmer Registration ID <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The registration ID of the farmer.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ request.farmer?.id_number || 'N/A' }}</p>
                                </v-card>
                            </v-col>
                            <v-col cols="3">
                                <v-card class="pa-4" color="primary-lighten1" elevation="0">
                                    <p class="font-weight-bold">Dimitra Farmer ID <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    size="16">mdi-information-outline</v-icon>
                                            </template>
                                            <span>The ID of the farmer.</span>
                                        </v-tooltip></p>
                                    <p class="">{{ request.farmer?.id || 'N/A' }}</p>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

        </div>
        <PlotDetails v-if="showPlotDetailsDialog" :dialog="showPlotDetailsDialog"
            :plot="selectedPlot" :requestId="requestId" :requestCountry="request?.country"
            @close="showPlotDetailsDialog = false" />

            <!-- Cancel Request confirmation (custom design) -->
        <v-dialog v-model="confirmCancelRequestDialog" persistent max-width="500" content-class="cancel-request-dialog">
            <v-card class="cancel-request-dialog-card">
                <v-toolbar color="primary" elevation="0" class="white--text">
                    <v-toolbar-title class="white--text font-weight-bold">Confirmation</v-toolbar-title>
                    <v-spacer />
                    <v-btn color="white" icon @click="confirmCancelRequestDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text class="pt-8 pb-6 text-center">
                    <div class="cancel-request-icon-wrap mb-4">
                        <v-icon size="48" color="orange">mdi-delete-outline</v-icon>
                    </div>
                    <p class="black--text mb-0">{{ $t('carbonCredit.plotTreeMapping.cancelRequestMessage') }}</p>
                </v-card-text>
                <v-divider />
                <v-card-actions class="pa-4">
                    <v-spacer />
                    <v-btn outlined color="primary" @click="confirmCancelRequestDialog = false">
                        {{ $t('carbonCredit.plotTreeMapping.cancel') }}
                    </v-btn>
                    <v-btn color="primary" class="white--text ml-3" depressed @click="confirmCancelRequest()">
                        DELETE
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- Mark as Completed confirmation (custom design) -->
        <v-dialog v-model="confirmMarkCompletedDialog" persistent max-width="500" content-class="mark-completed-dialog">
            <v-card class="mark-completed-dialog-card">
                <v-toolbar color="primary" elevation="0" class="white--text">
                    <v-toolbar-title class="white--text font-weight-bold">Confirmation</v-toolbar-title>
                    <v-spacer />
                    <v-btn color="white" icon @click="confirmMarkCompletedDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text class="pt-8 pb-6 text-center">
                    <div class="mark-completed-icon-wrap mb-4">
                        <v-icon size="56" color="primary">mdi-check-circle-outline</v-icon>
                    </div>
                    <p class="black--text mb-0">{{ $t('carbonCredit.plotTreeMapping.markAsCompletedMessage') }}</p>
                </v-card-text>
                <v-divider />
                <v-card-actions class="pa-4">
                    <v-spacer />
                    <v-btn outlined color="primary" @click="confirmMarkCompletedDialog = false">
                        {{ $t('carbonCredit.plotTreeMapping.cancel') }}
                    </v-btn>
                    <v-btn color="primary" class="white--text ml-3" depressed @click="confirmMarkCompleted()">
                        CONFIRM
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- Remove plot confirmation (custom design with delete icon) -->
        <v-dialog v-model="confirmDeleteDialog" persistent max-width="500" content-class="remove-plot-dialog">
            <v-card class="remove-plot-dialog-card">
                <v-toolbar color="primary" elevation="0" class="white--text">
                    <v-toolbar-title class="white--text font-weight-bold">Confirmation</v-toolbar-title>
                    <v-spacer />
                    <v-btn color="white" icon @click="confirmDeleteDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text class="pt-8 pb-6 text-center">
                    <div class="remove-plot-icon-wrap mb-4">
                        <v-icon size="48" color="orange">mdi-delete-outline</v-icon>
                    </div>
                    <p class="black--text mb-0">{{ $t('carbonCredit.plotTreeMapping.removePlotMessage') }}</p>
                </v-card-text>
                <v-divider />
                <v-card-actions class="pa-4">
                    <v-spacer />
                    <v-btn outlined color="primary" @click="confirmDeleteDialog = false">
                        {{ $t('carbonCredit.plotTreeMapping.cancel') }}
                    </v-btn>
                    <v-btn color="primary" class="white--text ml-3" depressed @click="confirmRemovePlot()">
                        DELETE
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import PlotDetails from './PlotDetails.vue';
import CarbonCreditService from '@/_services/CarbonCreditService';
import loadingMixin from '@/mixins/LoadingMixin';
import dateMixin from '@/mixins/DateMixin';
import GmapCluster from 'vue2-google-maps/dist/components/cluster';
import { DEFAULT_PLOT_SVG_RAW, SELECTED_PLOT_SVG_RAW } from '@/utils/plotMarkerIconSvgs';

const PLOT_MARKER_CATEGORY_COLORS = {
    yellow: '#F9A825',
    blue: '#1976D2',
    green: '#2E7D32',
};

const PLOT_MAP_MARKER_PIXEL_SIZE = 40;

export default {
    components: {
        PlotDetails,
        GmapCluster,
    },
    mixins: [loadingMixin, dateMixin],
    async mounted() {
        await this.getRequest();
    },
    data() {
        return {
            requestId: this.$route.params.id,
            showPlotDetailsDialog: false,
            breadcrumbs: [
                { text: 'Dashboard', disabled: false },
                { text: 'Carbon Credit', disabled: false },
                { text: 'Plot Tree Mapping', disabled: false },
                { text: 'Request Details', disabled: true },
            ],
            request: {
                // Will be populated from API
            },
            plotHeaders: [
                { text: 'Plot Number', value: 'plot_no' },
                { text: 'Longitude / Latitude', value: 'coordinates' },
                { text: 'Plot Radius', value: 'radius' },
                { text: 'Number of Trees', value: 'no_of_trees' },
                { text: 'Status', value: 'status' },
                { text: '', value: 'actions', sortable: false },
            ],
            expandedPanels: [0, 1, 2, 3, 4],
            selectedPlot: null,
            confirmCancelRequestDialog: false,
            confirmMarkCompletedDialog: false,
            confirmDeleteDialog: false,
            confirmDeleteData: null,
            requestRefreshKey: 0,
            defaultPlotIcon: '/assets/icons/defaultPlot.svg',
            selectedPlotIcon: '/assets/icons/selectedPlot.svg',
        };
    },
    computed: {
        plotsForMap() {
            const plots = this.request?.plots || [];
            return plots.filter(p => {
                const lat = parseFloat(p.latitude);
                const lng = parseFloat(p.longitude);
                return Number.isFinite(lat) && Number.isFinite(lng);
            });
        },
        plotMarkerLegendItems() {
            const raw = SELECTED_PLOT_SVG_RAW;
            const c = PLOT_MARKER_CATEGORY_COLORS;
            return [
                {
                    key: 'measured',
                    iconDataUrl: this.coloredPlotIconDataUrl(raw, c.blue),
                    labelKey: 'carbonCredit.plotTreeMapping.measuredPlot',
                },
                {
                    key: 'suggested',
                    iconDataUrl: this.coloredPlotIconDataUrl(raw, c.yellow),
                    labelKey: 'carbonCredit.plotTreeMapping.suggestedPlot',
                },
                {
                    key: 'audited',
                    iconDataUrl: this.coloredPlotIconDataUrl(raw, c.green),
                    labelKey: 'carbonCredit.plotTreeMapping.auditedPlot',
                },
            ];
        },
    },
    methods: {
        plotTreeMappingApiContext() {
            return {
                country: this.request?.country,
                request: this.request,
            };
        },
        async getRequest() {
            this.startLoading();
            const response = await CarbonCreditService.getPlotTreeMappingRequestById(this.requestId, {});
            this.request = response.data;
            this.stopLoading();
            this.$nextTick(() => this.fitPlotViewMapToBounds());
        },
        statusColor(status) {
            switch (status) {
                case 'completed':
                case 'approved':
                case 'submitted':
                    return 'success';
                case 'cancelled':
                case 'rejected':
                    return 'error';
                case 'pending':
                    return 'grey';
                case 'in_progress':
                case 'partially_submitted':
                    return 'warning';
                default:
                    return 'grey';
            }
        },
        openCancelRequestConfirm() {
            this.confirmCancelRequestDialog = true;
        },
        async confirmCancelRequest() {
            this.confirmCancelRequestDialog = false;
            try {
                this.startLoading();
                await CarbonCreditService.deletePlotTreeMappingRequest(this.requestId);
                this.$notify({ type: 'success', text: 'Request cancelled successfully' });
                this.$router.push({ name: 'plotTreeMapping' });
            } catch (error) {
                const msg = error?.data?.msg || error?.message || 'Failed to cancel request';
                this.$notify({ type: 'error', text: msg });
            } finally {
                this.stopLoading();
            }
        },
        goBack() {
            this.$router.go(-1);
        },
        editRequest() {
            this.$router.push({ name: 'treePlotAdd', params: { id: this.requestId } });
        },
        async download(type) {
    const mimeMap = {
        pdf: 'application/pdf',
        xls: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    };

    const extensionMap = {
        pdf: 'pdf',
        xls: 'xlsx'
    };

    try {
        this.startLoading();
        const response = await CarbonCreditService.downloadReportDetails(this.requestId, type);
        const blob = new Blob([response.data], { type: mimeMap[type] });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `plot-report-details-${this.requestId}.${extensionMap[type]}`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }, 100);

        this.$notify({ type: 'success', text: `${type.toUpperCase()} downloaded successfully` });
    } catch (error) {
        this.$notify({ type: 'error', text: `Failed to download ${type.toUpperCase()}` });
    } finally {
        this.stopLoading();
    }
}
,
        openMarkCompletedConfirm() {
            this.confirmMarkCompletedDialog = true;
        },
        async confirmMarkCompleted() {
            this.confirmMarkCompletedDialog = false;
            try {
                this.startLoading();
                await CarbonCreditService.updatePlotTreeMapping(this.requestId, { status: 'completed' });
                this.$notify({ type: 'success', text: 'Request marked as completed' });
                this.$router.push({ name: 'plotTreeMapping' });
            } catch (error) {
                const msg = error?.data?.msg || error?.message || 'Failed to mark request as completed';
                this.$notify({ type: 'error', text: msg });
            } finally {
                this.stopLoading();
            }
        },
        viewPlot(plot) {
            this.selectedPlot = plot;   
            this.showPlotDetailsDialog = true;
        },
        async downloadPlotReport(plot, fileType) {
            try {
                this.startLoading();
                const response = await CarbonCreditService.downloadPlotDetails(this.requestId, plot.id, fileType);
                const extensionMap = { pdf: 'pdf', xls: 'xlsx' };
                const blob = new Blob([response.data], {
                    type: fileType === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `plot-tree-mapping-${this.requestId}-${plot.id}.${extensionMap[fileType]}`);
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }, 100);
                this.$notify({ type: 'success', text: `${fileType.toUpperCase()} downloaded successfully` });
            } catch (error) {
                this.$notify({ type: 'error', text: `Failed to download ${fileType.toUpperCase()}` });
            } finally {
                this.stopLoading();
            }
        },
        deletePlot(plot) {
            this.confirmDeleteData = plot;
            this.confirmDeleteDialog = true;
        },
        viewAssignee(assignee) {
            this.$router.push({ name: 'plotTreeMappingAssignee', params: { id: assignee.id } });
        },
        getPlotStatus(plot) {
            return (plot && (plot.plotStatus && plot.plotStatus.status)) || plot?.status || null;
        },
        getPlotMarkerCategory(plot) {
            const plotNo = String(plot?.plot_no ?? '').trim();
            const firstLetter = /^[A-Za-z]/.test(plotNo) ? plotNo.charAt(0).toUpperCase() : '';
            if (firstLetter === 'J') return 'green';
            if (firstLetter === 'M') return 'blue';
            if (firstLetter === 'S') return 'yellow';

            const status = String(this.getPlotStatus(plot) || '').toLowerCase();
            if (status === 'audited') return 'green';
            if (status === 'submitted' || status === 'partially_submitted' || status === 'in_progress') {
                return 'blue';
            }
            if (!status || status === 'pending') return 'yellow';
            return null;
        },
        coloredPlotIconDataUrl(svgRaw, colorHex) {
            const svg = svgRaw
                .replace(/#34B0F5/g, colorHex)
                .replace(/#566DE3/g, colorHex);
            return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
        },
        /** Matches AddPlotTree legend: selected-style pin with tick for categorized plots. */
        getPlotMapIcon(plot) {
            const isSelected = true;
            const category = this.getPlotMarkerCategory(plot);
            let url;
            if (!category) {
                url = isSelected ? this.selectedPlotIcon : this.defaultPlotIcon;
            } else {
                const raw = isSelected ? SELECTED_PLOT_SVG_RAW : DEFAULT_PLOT_SVG_RAW;
                url = this.coloredPlotIconDataUrl(raw, PLOT_MARKER_CATEGORY_COLORS[category]);
            }
            const s = PLOT_MAP_MARKER_PIXEL_SIZE;
            return {
                url,
                scaledSize: new window.google.maps.Size(s, s),
                origin: new window.google.maps.Point(0, 0),
                anchor: new window.google.maps.Point(s / 2, s),
            };
        },
        fitPlotViewMapToBounds() {
            this.$nextTick(() => {
                if (!this.plotsForMap.length || !this.$refs.plotViewGmap) return;
                this.$refs.plotViewGmap.$mapPromise.then(map => {
                    const bounds = new window.google.maps.LatLngBounds();
                    this.plotsForMap.forEach(plot => {
                        bounds.extend({
                            lat: parseFloat(plot.latitude),
                            lng: parseFloat(plot.longitude),
                        });
                    });
                    map.fitBounds(bounds);
                });
            });
        },
        getPlotStatusClass(status) {
            switch (status) {
                case 'submitted':
                    return 'green-button';
                case 'pending':
                    return 'gray-button';
                case 'in_progress':
                case 'partially_submitted':
                case 'audited':
                    return 'orange-button';
                default:
                    return 'gray-button';
            }
        },
        getRequestStatusClass(status) {
            switch (status) {
                case 'submitted':
                case 'completed':
                    return 'green-button';
                case 'cancelled':
                    return 'red-button';
                case 'pending':
                    return 'gray-button';
                case 'in_progress':
                case 'partially_submitted':
                    return 'orange-button';
                default:
                    return 'gray-button';
            }
        },
        formatStatus(status) {
            if (!status) return 'N/A';
            const statusMap = {
                'pending': 'Pending',
                'in_progress': 'Partially Submitted',
                'partially_submitted': 'Partially Submitted',
                'submitted': 'Submitted',
                'audited': 'Audited',
                'completed': 'Completed',
                'cancelled': 'Cancelled',
                'approved': 'Approved',
                'rejected': 'Rejected'
            };
            return statusMap[status] || status;
        },
        async confirmRemovePlot() {
            const plotToRemove = this.confirmDeleteData;
            this.confirmDeleteDialog = false;
            if (!plotToRemove?.id) return;
            try {
                this.startLoading();
                await CarbonCreditService.deleteTreePlot(plotToRemove.id);
                this.stopLoading();
                this.$notify({ type: 'success', text: 'Plot removed successfully. Refreshing...' });
                setTimeout(() => {
                    window.location.reload();
                }, 300);
            } catch (error) {
                const msg = error?.data?.msg || error?.message || 'Failed to remove plot';
                this.$notify({ type: 'error', text: msg });
            } finally {
                this.stopLoading();
            }
        },
    },
};
</script>

<style scoped>
.cancel-request-dialog-card,
.mark-completed-dialog-card,
.remove-plot-dialog-card {
    background: #fff !important;
}
.remove-plot-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    background-color: #faf8f5;
    border-radius: 12px;
}
.mark-completed-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 88px;
    height: 88px;
    background-color: #e8f5e9;
    border-radius: 12px;
}
.cancel-request-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    background-color: #faf8f5;
    border-radius: 12px;
}

.v-card {
    background: #f9fdfb;
}

.v-card-title {
    font-weight: bold;
    font-size: 16px;
}

.v-text-field,
.v-textarea {
    background: #eafaf3 !important;
}

.v-chip {
    font-weight: 500;
    font-size: 13px;
    text-transform: none;
}

/* Status button styles (same as carbon-credit users List.vue) */
.green-button {
    background-color: #c1ffe7 !important;
    border: 2px solid #00bd73 !important;
    color: #489e16 !important;
}
.red-button {
    background-color: #fdecea !important;
    border: 2px solid #f03737 !important;
    color: #f03737 !important;
}
.gray-button {
    background-color: #e8e8e8 !important;
    border: 2px solid #000000 !important;
    color: #000000 !important;
}
.orange-button {
    background-color: #fff7ec !important;
    border: 2px solid #ffa826 !important;
    color: #ffa826 !important;
}

.primary-lighten1 {
    background-color: #eafaf3;
}

.plot-table-map-divider {
    margin-top: 22px !important;
    margin-bottom: 28px !important;
    border-color: rgba(0, 0, 0, 0.12) !important;
}

.plot-map-below-table {
    margin-top: 4px;
}

.plot-marker-legend-icon {
    width: 40px;
    height: 40px;
    margin-right: 5px;
    object-fit: fill;
}

.plot-tree-status-legend.status-legend-section {
    background: #f8f9fa;
    border-radius: 10px;
    flex-wrap: wrap;
    font-size: 15px;
}

.plot-tree-status-legend .legend-title {
    color: #222;
}

.plot-tree-status-legend .legend-item {
    white-space: nowrap;
    font-size: 15px;
    color: #222;
}
</style>
